import { Navigate, Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';
import { fmtTs, fmtBps } from './AlphaOmegaDashboardPage';
import { signalTypeColor } from './AlphaOmegaSectorSignalsPage';

// ── Types ────────────────────────────────────────────────────────────────────

interface SectorSignalResponse {
  signal_id: string;
  sector: string;
  signal_type: string;
  confidence_bps: number;
  summary: string;
  captured_at: number;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function InfoRow({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '160px 1fr', gap: 8,
      padding: '10px 16px',
      borderBottom: '1px solid rgba(255,255,255,0.05)',
    }}>
      <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'var(--omega-mono)' }}>{label}</span>
      <span style={{ fontSize: '9pt', color: color ?? 'var(--omega-ink)', fontFamily: 'var(--omega-mono)' }}>{value}</span>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function AlphaOmegaSectorSignalDetailPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { signal_id = '' } = useParams<{ signal_id: string }>();

  const { data, isPending, isError } = useQuery({
    queryKey: ['alpha-omega', 'sector-signal', signal_id],
    queryFn: () => apiFetch<SectorSignalResponse>(`/alpha-omega/sector-signals/${signal_id}`),
    refetchInterval: 30_000,
    enabled: !!signal_id,
  });

  const crumbLabel = data?.sector ?? signal_id.slice(0, 12);

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA',        to: '/omega' },
        { label: 'Alpha-Omega',  to: '/omega/alpha-omega' },
        { label: 'Market signals',    to: '/omega/alpha-omega/sector-signals' },
        { label: crumbLabel },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          {data ? data.sector : 'Loading…'}
        </h1>
        {data && (
          <span style={{
            fontSize: '7.5pt', padding: '2px 8px',
            border: `1px solid ${signalTypeColor(data.signal_type)}`,
            color: signalTypeColor(data.signal_type),
            letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, borderRadius: 3,
          }}>
            {data.signal_type}
          </span>
        )}
        <Link
          to="/omega/alpha-omega/sector-signals"
          style={{
            marginLeft: 'auto', background: 'rgba(125,211,252,0.12)',
            border: '1px solid rgba(125,211,252,0.35)', borderRadius: 4,
            color: '#7dd3fc', fontFamily: 'var(--omega-mono)', fontSize: '8.5pt',
            fontWeight: 600, padding: '5px 12px', textDecoration: 'none',
          }}
        >
          ← Back to signals
        </Link>
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError   && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading signal.</div>}

      {data && (
        <div style={{
          background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 6, overflow: 'hidden', maxWidth: 560,
        }}>
          <InfoRow label="ID"      value={data.signal_id} />
          <InfoRow label="Sector"       value={data.sector} />
          <InfoRow label="Type"       value={data.signal_type} color={signalTypeColor(data.signal_type)} />
          <InfoRow label="Confidence"    value={fmtBps(data.confidence_bps)} />
          <InfoRow label="Summary"     value={data.summary} />
          <InfoRow label="Recorded"      value={fmtTs(data.captured_at)} />
        </div>
      )}
    </div>
  );
}
